import { ApolloClient, NormalizedCacheObject } from "@apollo/client";
import React, { createContext, useContext, useReducer } from "react";
import { Building } from "../graphql/models";
import { ApplicationMenuEntry, ServiceCalendarRoute } from "../hooks";

const UserStateContext: React.Context<{
  isAuthenticated?: boolean;
  userRole?: string;
  userBuilding?: Building;
  userBuildings?: Building[];
  apolloClient?: ApolloClient<NormalizedCacheObject>;
  serviceCalendarRoutes?: ServiceCalendarRoute[];
  applicationMenuServiceRoutes?: ApplicationMenuEntry[];
}> = createContext({});
const UserDispatchContext = createContext({});

// -- Storage Keys -- //
export enum UserStorageKeys {
  USER_TOKEN = "titiro_app_user_token",
  USER_ROLE = "titiro_app_user_role",
  USER_BUILDING = "titiro_app_user_building",
  USER_COMPATIBLE_BUILDINGS = "titiro_app_user_compatible_buildings",
}

// -- Reducers -- //
export enum UserReducerActions {
  LOGIN_SUCCESS,
  LOGOUT_SUCCESS,
  SET_USER_BUILDING,
  SET_USER_BUILDINGS_LIST,
  SET_SERVICE_CONTESTUAL_DATA,
}

function userReducer(
  state,
  action: {
    type: UserReducerActions;
    userRole?: string;
    userBuilding?: Building;
    userBuildings?: Building[];
    serviceCalendarRoutes?: ServiceCalendarRoute[];
    applicationMenuServiceRoutes?: ApplicationMenuEntry[];
  }
) {
  switch (action.type) {
    case UserReducerActions.LOGIN_SUCCESS:
      return {
        ...state,
        isAuthenticated: true,
        userRole: action.userRole,
        userBuilding: action.userBuilding,
        userBuildings: action.userBuildings,
      };
    case UserReducerActions.LOGOUT_SUCCESS:
      const { apolloClient } = state;
      (apolloClient as ApolloClient<NormalizedCacheObject>).clearStore();
      return {
        ...state,
        isAuthenticated: false,
        userRole: void 0,
        userBuilding: void 0,
        userBuildings: void 0,
      };
    case UserReducerActions.SET_USER_BUILDINGS_LIST:
      return {
        ...state,
        userBuildings: action.userBuildings,
      };
    case UserReducerActions.SET_USER_BUILDING:
      return {
        ...state,
        userBuilding: action.userBuilding,
      };
    case UserReducerActions.SET_SERVICE_CONTESTUAL_DATA:
      return {
        ...state,
        serviceCalendarRoutes: action.serviceCalendarRoutes,
        applicationMenuServiceRoutes: action.applicationMenuServiceRoutes,
      };
    default:
      throw new Error(`Unhandled action provided: ${action.type}`);
  }
}

// -- React Providers -- //
function UserProvider({ children, apolloClient }) {
  const [state, dispatch] = useReducer(userReducer, {
    isAuthenticated: !!sessionStorage.getItem(UserStorageKeys.USER_TOKEN),
    userRole: sessionStorage.getItem(UserStorageKeys.USER_ROLE),
    userBuilding:
      sessionStorage.getItem(UserStorageKeys.USER_BUILDING) !== null
        ? JSON.parse(
            sessionStorage.getItem(UserStorageKeys.USER_BUILDING) as string
          )
        : void 0,
    userBuildings:
      sessionStorage.getItem(UserStorageKeys.USER_COMPATIBLE_BUILDINGS) !== null
        ? JSON.parse(
            sessionStorage.getItem(
              UserStorageKeys.USER_COMPATIBLE_BUILDINGS
            ) as string
          )
        : void 0,
    apolloClient,
  });

  return (
    <UserStateContext.Provider value={state}>
      <UserDispatchContext.Provider value={dispatch}>
        {children}
      </UserDispatchContext.Provider>
    </UserStateContext.Provider>
  );
}

// -- Context Hooks -- //
function useUserState() {
  const context = useContext(UserStateContext);
  if (!context) {
    throw new Error("useUserState can only be used within a UserProvider");
  }

  return context;
}

function useUserDispatch() {
  const context = useContext(UserDispatchContext);
  if (!context) {
    throw new Error("useUserDispatch can only be used within a UserProvider");
  }

  return context;
}

// -- Handles -- //
function setUserLoggedIn(dispatch, userToken, userRole, userBuildings?) {
  const userBuilding = userBuildings ? userBuildings[0] : void 0;
  sessionStorage.setItem(UserStorageKeys.USER_TOKEN, userToken);
  sessionStorage.setItem(UserStorageKeys.USER_ROLE, userRole);
  if (userBuildings) {
    sessionStorage.setItem(
      UserStorageKeys.USER_COMPATIBLE_BUILDINGS,
      JSON.stringify(userBuildings)
    );
  }
  if (
    userBuilding &&
    (userRole === "concierge" || userRole === "building_manager")
  ) {
    sessionStorage.setItem(
      UserStorageKeys.USER_BUILDING,
      JSON.stringify(userBuilding)
    );
  }

  dispatch({
    type: UserReducerActions.LOGIN_SUCCESS,
    userRole,
    userBuilding,
    userBuildings,
  });
}

function setUserLoggedOut(dispatch) {
  sessionStorage.removeItem(UserStorageKeys.USER_TOKEN);
  sessionStorage.removeItem(UserStorageKeys.USER_ROLE);
  sessionStorage.removeItem(UserStorageKeys.USER_BUILDING);
  sessionStorage.removeItem(UserStorageKeys.USER_COMPATIBLE_BUILDINGS);
  dispatch({
    type: UserReducerActions.LOGOUT_SUCCESS,
  });
}

function setUserBuilding(dispatch, userBuilding) {
  if (userBuilding) {
    sessionStorage.setItem(
      UserStorageKeys.USER_BUILDING,
      JSON.stringify(userBuilding)
    );
  } else {
    sessionStorage.removeItem(UserStorageKeys.USER_BUILDING);
  }
  dispatch({
    type: UserReducerActions.SET_USER_BUILDING,
    userBuilding,
  });
}

function setUserBuildings(dispatch, userBuildings: Partial<Building>[]) {
  dispatch({
    type: UserReducerActions.SET_USER_BUILDINGS_LIST,
    userBuildings,
  });
}

function setServiceContestualData(
  dispatch,
  serviceCalendarRoutes,
  applicationMenuServiceRoutes
) {
  dispatch({
    type: UserReducerActions.SET_SERVICE_CONTESTUAL_DATA,
    serviceCalendarRoutes,
    applicationMenuServiceRoutes,
  });
}

export {
  UserProvider,
  useUserState,
  useUserDispatch,
  setUserLoggedIn,
  setUserLoggedOut,
  setUserBuilding,
  setUserBuildings,
  setServiceContestualData,
};
